import React, { FC, useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Carousel as BootstrapCarousel } from 'bootstrap';
import { Container } from '@components/atoms/Container';
import { Anchor, AnchorMode } from '@components/atoms/Anchor';
import { Typography, TypographyAppearanceVariants, TypographyTagVariants } from '@components/atoms/Typography';
import styles from './Carousel.module.css';
import { useTranslation } from 'react-i18next';
import { useThemes } from '@hooks/useThemes';
import { FinancingLogo } from '../FinancingLogo';

export interface CarouselProps {
    items: {
        theme?: 'dark' | 'light';
        desktopImg?: { src?: string; type?: string; width?: number; height?: number };
        mobileImg?: { src?: string; type?: string; width?: number; height?: number };
        category: string;
        title: string;
        subtitle?: string;
        description?: string;
        link: { label: string; url: string };
        footerNote?: string;
    }[];
    mode: 'boxed' | 'wide';
    includeLogos: boolean;
}

export const Carousel: FC<CarouselProps> = ({ items, mode, includeLogos }) => {
    const { t } = useTranslation();

    const translations = {
        previous: t('carousel-component.previous'),
        next: t('carousel-component.next'),
    };

    const [carousel, setCarousel] = useState<BootstrapCarousel | null>(null);

    const carouselRef = useCallback((node: HTMLDivElement) => {
        if (node !== null) {
            const carouselInstance = BootstrapCarousel.getOrCreateInstance(node, { ride: 'carousel' });
            setCarousel(carouselInstance);
        }
    }, []);

    const [wideCarouselHeight, setWideCarouselHeight] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            const headerHeight = document.querySelector('#sticky-header')?.clientHeight || 0;
            setWideCarouselHeight(window.innerHeight - headerHeight);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <Container classes={mode === 'boxed' ? 'px-3' : 'px-0'} type={mode === 'boxed' ? 'default' : 'fluid'}>
            <div ref={carouselRef} className={`carousel ${styles.carousel} slide`} data-bs-ride="carousel" data-testid="carousel">
                <div className="carousel-inner">
                    {items.map((item, index) => (
                        <div key={`carousel-item-${index}`} className={`carousel-item ${index === 0 ? 'active' : ''}`}>
                            <picture>
                                <source
                                    media="(min-width: 768px)"
                                    srcSet={item.desktopImg?.src}
                                    style={{ height: mode === 'wide' ? `${wideCarouselHeight}px` : undefined }}
                                />
                                <img
                                    src={item.mobileImg?.src}
                                    alt={item.title}
                                    style={{ height: mode === 'wide' ? `${wideCarouselHeight}px` : undefined }}
                                />
                            </picture>
                            <Container
                                classes={`${styles['text-over-carousel']} ${mode === 'boxed' ? styles['text-over-carousel-boxed'] : ''}`}>
                                <div className="row text-start">
                                    <div className="col-10 col-lg-7">
                                        <Typography
                                            classes="mb-2 mb-sm-0"
                                            color={item.theme === 'dark' ? 'light' : 'dark'}
                                            fontSize="10"
                                            breakpointsStyles={{ sm: { fontSize: '12' } }}
                                            styles={{ spacedText: true }}>
                                            {item.category}
                                        </Typography>
                                        <Typography
                                            classes="lh-sm mb-3"
                                            color={item.theme === 'dark' ? 'light' : 'dark'}
                                            appearanceLevel={TypographyAppearanceVariants.level1}
                                            breakpointsStyles={{ sm: { fontSize: '54' }, md: { fontSize: '46' } }}>
                                            {item.title}
                                        </Typography>
                                        {item.subtitle && (
                                            <Typography
                                                tagVariant={TypographyTagVariants.span}
                                                classes={`${styles.subtitle} d-block mb-3`}
                                                color={item.theme === 'dark' ? 'light' : 'dark'}>
                                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.subtitle) }} />
                                            </Typography>
                                        )}
                                        {item.description && (
                                            <Typography
                                                classes="mb-3"
                                                color={item.theme === 'dark' ? 'light' : 'dark'}
                                                fontSize="16">
                                                {item.description}
                                            </Typography>
                                        )}
                                        <div className="mt-2">
                                            <Anchor
                                                href={item.link.url}
                                                mode={AnchorMode.secondary}
                                                classes="me-md-2"
                                                textColor={item.theme === 'dark' ? 'light' : undefined}>
                                                {item.link.label}
                                            </Anchor>
                                        </div>
                                    </div>
                                </div>
                            </Container>
                            {item.footerNote && (
                                <Container classes={`${styles['footer-over-carousel']} py-2 py-lg-4`}>
                                    <Typography color={item.theme === 'dark' ? 'light' : 'dark'} classes="pb-2 my-2">
                                        {item.footerNote}
                                    </Typography>
                                </Container>
                            )}
                        </div>
                    ))}
                </div>
                <Container>
                    <div className="position-relative">
                        {includeLogos && <LogosDisplay />}
                        {/* controls: previous and next */}
                        {items.length > 1 && (
                            <div className={`carousel-controls-container ${styles['carousel-controls-container']}`}>
                                <button
                                    className={`carousel-control-prev ${styles['carousel-control-prev']}`}
                                    onClick={() => carousel?.prev()}>
                                    <span
                                        className={`carousel-control-prev-icon ${styles['carousel-control-prev-icon']}`}
                                        aria-hidden="true">
                                        <Typography tagVariant={TypographyTagVariants.span}>
                                            <i className="fa-light fa-arrow-left" />
                                        </Typography>
                                    </span>
                                    <span className="visually-hidden">{translations.previous}</span>
                                </button>
                                <button
                                    className={`carousel-control-next ${styles['carousel-control-next']}`}
                                    onClick={() => carousel?.next()}>
                                    <span
                                        className={`carousel-control-next-icon ${styles['carousel-control-next-icon']}`}
                                        aria-hidden="true">
                                        <Typography tagVariant={TypographyTagVariants.span}>
                                            <i className="fa-light fa-arrow-right" />
                                        </Typography>
                                    </span>
                                    <span className="visually-hidden">{translations.next}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </Container>
            </div>
        </Container>
    );
};

const LogosDisplay: FC = () => {
    const { useGrayscaleTheme } = useThemes();

    return (
        <div
            className={
                // themes
                `${useGrayscaleTheme ? styles['theme-grayscale'] : ''}` +
                // other classes
                ` ${styles['content-next-to-carousel-controls']} d-none d-lg-block align-content-center px-4`
            }>
            <FinancingLogo />
        </div>
    );
};
